import {type CSSProperties} from 'react';
import {type SxProps, Theme} from '@mui/material';
import choicesSectionImage from '$assets/images/thankyou-booking.jpg';
import theme from '$modules/booking/theme';
import {scrollbarStyles} from '$styles/index';
import {BookingScreenColors} from './constant';
import {Color, Font} from '$constants/style';

const styles: Record<string, SxProps> = {
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(7, 0, 3),
    background: BookingScreenColors.WrapperBackground,

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(7, 0),
    },
  },
  sectionWrapper: {
    flex: 1,
    display: 'flex',
    marginTop: theme.spacing(0),

    [theme.breakpoints.up('lg')]: {
      overflowX: 'hidden',
      overflowY: 'auto',

      ...scrollbarStyles,
    },
  },
  sectionWrapperWithStepper: {
    flex: 1,
    display: 'flex',
    marginTop: theme.spacing(5),
    width: '100%',
    justifyContent: 'center',

    [theme.breakpoints.up('lg')]: {
      overflowX: 'hidden',
      overflowY: 'auto',
      marginTop: '-4.5rem',

      ...scrollbarStyles,
    },
  },
  section: {
    flex: 1,
    padding: theme.spacing(0, 3),
    height: 'auto',
    [theme.breakpoints.up('lg')]: {
      padding: '0 13.5rem',
      overflow: 'hidden',
    },
  },
  scrollSection: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '60vh',
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      maxHeight: '50vh',
    },
  },
  title: {
    color: theme.palette.text.tertiary,
    paddingBottom: theme.spacing(1),
    fontSize: '1.5rem',
    fontWeight: theme.typography.fontWeightBold,

    [theme.breakpoints.up('lg')]: {
      fontSize: '2.5rem',
    },
  },
  subTitle: {
    color: theme.palette.text.tertiary,
    letterSpacing: '-0.75px',
    padding: theme.spacing(0),

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 7),
    },
  },
  subHeading: {
    fontSize: '1.5rem',
    fontWeight: theme.typography.fontWeightBold,

    [theme.breakpoints.up('lg')]: {
      fontSize: '2rem',
    },
  },
  subTitleSeeAll: {
    position: 'relative',
    cursor: 'pointer',
    background: BookingScreenColors.SeeAllGradient,
    letterSpacing: '-0.75px',
    padding: theme.spacing(0),
    textAlign: 'center',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    '&::after': {
      position: 'absolute',
      background: BookingScreenColors.SeeAllGradient,
      content: '""',
      height: '1px',
      width: '50px',
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: '0',
    },

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 7),
    },
  },
  stepsTypeWrap: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(4),

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      gridGap: theme.spacing(14),
    },
  },

  stepsTypeHead: {
    fontWeight: 600,
    fontSize: '1.125rem',
    letterSpacing: '-0.85px',

    [theme.breakpoints.up('lg')]: {
      fontSize: '1.5rem',
    },
  },
  footerSection: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 3),
    gridGap: theme.spacing(6),

    [theme.breakpoints.up('lg')]: {
      padding: '0 13.5rem',
      gridGap: theme.spacing(3),
    },
  },
};

export const registerWithStoneyGateViewStyles: Record<string, SxProps> = {
  radioContainer: {
    justifyContent: 'flex-start',
    maxWidth: '24rem',
  },
  mutedText: {
    color: theme.palette.text.disabled,
    maxWidth: '24rem',
  },
  circleIcon: {
    borderRadius: '50%',
    alignItems: 'center',
    display: 'flex',
    position: 'absolute',
    right: 1,
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  inputContainer: {
    maxWidth: '24rem',
  },
  otpBox: {
    width: '50px',
    backgroundColor: Color.White,
    borderRadius: '4px',
  },
  otpInfoText: {
    mb: '20px',
  },
  verifyBtn: {
    width: '170px',
    marginTop: '1rem',
  },
  sendAgainBtn: {
    cursor: 'pointer',
  },
  resetBtn: {
    marginRight: '10px',
  },
  endIcon: {
    color: Color.Black,
    cursor: 'pointer',
  },
  forgotPasswordText: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: '0.90rem',
    marginTop: theme.spacing(1),
    userSelect: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
};

export const careSelectionViewStyles: Record<string, SxProps> = {
  menuItemContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },

  menuItemTitle: {
    color: BookingScreenColors.MenuItemTitle,
    fontWeight: 400,
    fontSize: '16px',
  },

  menuItemCaption: {
    textWrap: 'wrap',
  },
};

export const consultantSelectionViewStyles: Record<string, CSSProperties> = {
  valueContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  menuItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '8px',
    overflowX: 'auto',
  },
  menuItemProfileImage: {
    height: '32px',
    width: '32px',
    borderRadius: '50%',
  },
  menuItemInitial: {
    backgroundColor: BookingScreenColors.MenuItemBackground,
    height: '32px',
    width: '32px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuItemBody: {
    flex: 1,
    display: 'flex',
    gap: '8px',
  },
  menuItemDetails: {
    flex: 1,
  },
  menuItemBioContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuItemBioText: {
    color: Color.Primary2Darkest,
    fontWeight: 300,
    textDecoration: 'none',
  },
  fontThin: {
    fontWeight: 300,
    textWrap: 'wrap',
  },
};

export const bookingSelectionViewStyles: Record<string, SxProps> = {
  section: {
    flex: 1,
    padding: theme.spacing(0, 3),
    [theme.breakpoints.down('md')]: {
      maxHeight: '80vh',
    },
    [theme.breakpoints.up('lg')]: {
      padding: '0 13.5rem',
      overflowX: 'hidden',
      overflowY: 'auto',

      ...scrollbarStyles,
    },
  },
};

export const choicesConfirmationViewStyles: Record<string, SxProps> = {
  screenSection: {
    display: 'flex',
    flex: 1,

    [theme.breakpoints.up('lg')]: {
      overflowX: 'hidden',
      overflowY: 'auto',
      ...scrollbarStyles,
    },
  },
  bookingWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    background: Color.BookingWrapperBackground,
    padding: theme.spacing(7.5, 2.5),

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(10),
      overflowX: 'hidden',
      overflowY: 'auto',
      ...scrollbarStyles,
    },
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: 1,
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      gridGap: '80px',
      overflowX: 'hidden',
      overflowY: 'auto',
      ...scrollbarStyles,
    },

    [theme.breakpoints.up('xl')]: {
      gridGap: '160px',
    },
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',

    '& .site-logo': {
      width: '52px',
      height: '100%',
    },

    '& .hosptial-logo': {
      width: '178px',
      height: '100%',
    },

    [theme.breakpoints.up('lg')]: {
      width: 'unset',
    },
  },

  hospitalLogoWrap: {
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    height: '100%',
    gridGap: theme.spacing(3),
    [theme.breakpoints.up('lg')]: {
      gridGap: theme.spacing(5),
      overflowX: 'hidden',
      overflowY: 'auto',
      ...scrollbarStyles,
    },
  },
  imageSection: {
    display: 'none',

    [theme.breakpoints.up('lg')]: {
      display: 'block',
      maxWidth: '430px',
      width: '430px',
      backgroundImage: `url(${choicesSectionImage})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
  },
};

export const patientCarerDetailsViewStyles: Record<string, SxProps> = {
  subTitle: {
    color: theme.palette.text.primary,
  },
};

export const AppSummaryViewStyles: Record<string, SxProps | CSSProperties> = {
  screenSection: {
    display: 'flex',
    flex: 1,

    [theme.breakpoints.up('lg')]: {
      overflowX: 'hidden',
      overflowY: 'auto',
      ...scrollbarStyles,
    },
  },

  bookingWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    background: Color.BookingWrapperBackground,
    padding: theme.spacing(7.5, 2.5),

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(10),
      overflowX: 'hidden',
      overflowY: 'auto',
      ...scrollbarStyles,
    },
  },

  appSummaryWrap: {
    display: 'flex',
    gridGap: theme.spacing(5),
    flex: 1,

    [theme.breakpoints.up('lg')]: {
      gridGap: theme.spacing(1),
      overflowX: 'hidden',
      overflowY: 'auto',
      ...scrollbarStyles,
    },
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',

    '& .site-logo': {
      width: '52px',
      height: '100%',
    },

    '& .hosptial-logo': {
      width: '178px',
      height: '100%',
    },

    [theme.breakpoints.up('lg')]: {
      width: 'unset',
    },
  },

  appSummarySection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gridGap: theme.spacing(4),

    [theme.breakpoints.up('lg')]: {
      gridGap: theme.spacing(6),
      ...scrollbarStyles,
    },
  },

  summaryActionBtnWrap: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(3),

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },

  summaryHead: {
    color: theme.palette.primary.main,
    fontSize: '2rem',

    [theme.breakpoints.up('lg')]: {
      fontSize: '3rem',
    },
  },

  summaryTitle: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },

  bookingActionBtn: {
    textTransform: 'none',

    '&.delete-button': {
      '& svg': {
        color: theme.palette.error.main,
      },
    },
  },

  // New styles for summary details
  summaryLabelText: {
    color: theme.palette.primary.main,
  },

  summaryValueText: {
    color: theme.palette.text.secondary,
  },

  // Styles for divider and logos
  divider: {
    height: '0.75rem',
    width: '1px',
    backgroundColor: '#EBC3FF',
  },

  hospitalTextLogo: {
    height: '6rem',
    width: '6rem',
  },

  warningButton: {
    backgroundColor: '#EBA300',
    '&:hover': {
      backgroundColor: '#D99700',
    },
  },
  appSummaryVideoSection: {
    display: 'none',
    position: 'relative',
    overflow: 'hidden',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },

    [theme.breakpoints.up('lg')]: {
      display: 'block',
      width: '39rem',
      maxWidth: '39rem',
    },
  },
  hospitalLogoWrap: {
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  modalWrapper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: Color.White,
    boxShadow: '0rem 0.25rem 1rem rgba(0, 0, 0, 0.1)',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '80%',
    maxWidth: '37.5rem',
    height: 'auto',
    '&:focus': {
      outline: 'none',
    },
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: '1rem',
  },
  modalTitle: {
    fontWeight: 600,
    fontSize: '1.6rem',
    color: Color.Grey700,
    padding: '0.1875rem 0.9375rem',
  },
  modalPurpleTitle: {
    fontWeight: 600,
    fontSize: '1.6rem',
    color: Color.Purple600,
    padding: '0.1875rem 0.9375rem',
  },
  modalDescription: {
    fontSize: '1rem',
    padding: '0 1.1875rem',
    marginBottom: '2.8125rem',
    color: Color.Grey600,
  },
  modalBtnWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    marginTop: 'auto',
    marginBottom: '0.375rem',
    '& button': {
      marginLeft: '1.25rem',
      minWidth: '8.75rem',
      borderRadius: '0.5rem',
      fontSize: '1.1rem',
    },
  },
  closeIcon: {
    cursor: 'pointer',
    color: Color.Grey600,
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
  },
  cancelBtn: {
    textTransform: 'none',
    color: theme.palette.error.main,
    backgroundColor: theme.palette.common.white,
    borderColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light,
    },
  },
  deleteBtn: {
    textTransform: 'none',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  bookingModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  bookingModalWrapper: {
    backgroundColor: Color.White,
    boxShadow: '0rem 0.25rem 1rem rgba(0, 0, 0, 0.1)',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '42rem',
    borderRadius: '0.5rem',
    '&:focus': {
      outline: 'none',
    },
  },

  bookingModalTitle: {
    fontWeight: 600,
    fontSize: '1.6rem',
    color: Color.Purple600,
    marginBottom: '0.75rem',
  },

  bookingModalDescription: {
    fontSize: '1.125rem',
    color: Color.TextPrimary,
  },

  bookingModalBoldDescription: {
    fontWeight: 600,
  },

  bookingModalActionButtonContainer: {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '2.25rem',
  },

  bookingModalActionButtonSecondary: {
    backgroundColor: Color.Grey200,
  },
};

export const patientContactDetailViewStyles: Record<string, SxProps> = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: theme.spacing(0, 3),
    gridGap: theme.spacing(4),

    [theme.breakpoints.up('lg')]: {
      gridGap: theme.spacing(6),
      padding: '0 13.5rem',
    },
    ...scrollbarStyles,
  },
  inputContainer: {
    maxWidth: '24rem',
  },
};

export const patientInsuranceDetailViewStyles: Record<
  string,
  SxProps | CSSProperties
> = {
  inputContainer: {
    maxWidth: '24rem',
  },
  paymentCardContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Color.White,
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    border: '1px solid',
    borderColor: Color.Grey800,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: theme.spacing(0, 3),
    gridGap: theme.spacing(4),
    width: '100%',
    maxWidth: '75%',
    margin: '0 auto',

    [theme.breakpoints.up('lg')]: {
      gridGap: theme.spacing(6),
      padding: '0 5rem',
    },
    ...scrollbarStyles,
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    maxWidth: '100%',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  formControl: {
    marginTop: 2,
    marginBottom: 1,
  },
  formLabel: {
    fontWeight: 500,
    color: '#333',
    marginBottom: 1,
  },
  inputField: {
    marginBottom: 2,
  },
  checkbox: {
    marginTop: 1,
  },
  paymentSection: {
    marginTop: 3,
    padding: 2,
    border: '1px solid #e0e0e0',
    borderRadius: 1,
    backgroundColor: '#f9f9f9',
  },
  paymentHeader: {
    fontWeight: 600,
    marginBottom: 2,
  },
  stripeContainer: {
    marginTop: 2,
    padding: 2,
    border: '1px solid #e0e0e0',
    borderRadius: 1,
    backgroundColor: Color.White,
  },
  loadingContainer: {
    height: '70vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorAlert: {
    marginTop: 2,
  },
  insuranceFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  acceptTerms: {
    marginTop: 2,
    display: 'flex',
    alignItems: 'flex-start',
  },
  summaryContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '25px',
  },

  icon: {
    width: 40,
    height: 40,
    color: '#FF9800',
  } as CSSProperties,
};

export const lastStepViewStyles: Record<string, SxProps> = {
  inputContainer: {
    maxWidth: '24rem',
  },
};

export const reviewCancelSummaryViewStyles: Record<string, SxProps<Theme>> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '654px',
    padding: '32px',
    height: '100%',
    justifyContent: 'space-between',
  },
  summaryContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  spacing: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  paymentSummaryBox: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '32px',
  },
  alertStyle: {
    width: '100%',
  },
  imageContainer: {
    position: 'relative',
    width: '136px',
    height: '136px',
    marginBottom: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  cancelIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '77px',
    height: '82px',
    zIndex: 2,
  },
  titleText: {
    mb: '20px',
    width: '100%',
    maxWidth: '630px',
    lineheight: '36px',
    fontSize: {xs: '24px', md: '32px'},
    lineHeight: '36px',
    letterSpacing: '-0.75%',
    fontFamily: Font.Lexend,
    fontWeight: 600,
    textAlign: 'center',
    mt: '20px',
    color: theme => theme.palette.primary.main,
  },
  descriptionText: {
    mb: '20px',
    maxWidth: '579px',
    color: theme => theme.palette.text.primary,
    fontSize: '18px',
    fontWeight: '400',
    fontFamily: 'Lexend, sans-serif',
    lineHeight: '22px',
    letterSpacing: '-0.75px',
    textAlign: 'center',
  },
  summaryTitle: {
    color: '#7625DC',
    fontWeight: 600,
    fontSize: '24px',
    fontFamily: Font.Lexend,
    lineHeight: '28px',
    letterSpacing: '-0.75px',
    mb: '20px',
  },
  summaryStack: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  primaryText: {
    color: theme => theme.palette.primary.main,
  },
  secondaryText: {
    color: theme => theme.palette.text.secondary,
  },
  dividerStyle: {
    my: 1,
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    mt: 5,
    width: '100%',
    gap: 1,
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 'auto',
    marginBottom: '32px',
    gap: '20px',
  },
  leftButtonBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100px',
    height: '40px',
    borderRadius: '8px',
    px: '24px',
    py: '16px',
    gap: '10px',
  },
  rightButtonBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '207px',
    height: '40px',
    borderRadius: '8px',
    px: '24px',
    py: '12px',
    gap: '12px',
    background:
      'linear-gradient(90deg, #yourGradientStartColor 0%, #yourGradientEndColor 100%)',
  },
  fullWidthBox: {
    width: '100%',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '76px',
    width: '100%',
  },
  loadingIcon: {
    color: Color.White,
    opacity: 0.8,
  },
};

export const cancelFormViewStyles: Record<
  string,
  SxProps<Theme> | CSSProperties
> = {
  container: {
    maxWidth: '600px',
    mx: 'auto',
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    paddingBottom: '20px',
  },
  imageContainer: {
    position: 'relative',
    width: 136,
    height: 136,
    mb: 2,
    alignSelf: 'center',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  cancelIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 77,
    height: 82,
  },
  titleText: {
    mb: 4,
    textAlign: 'center',
    fontSize: '32px',
    fontWeight: 600,
    color: Color.PrimaryMain,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  radioGroupContainer: {
    width: '100%',
    maxWidth: '500px',
    display: 'flex',
    flexDirection: 'column',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControlLabel-label': {
      fontSize: '16px',
      fontWeight: 400,
      color: Color.TextPrimary,
    },
  },
  otherTextContainer: {
    mt: 2,
    width: '100%',
  },
  textField: {
    backgroundColor: 'white',
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    },
  },
  buttonsContainer: {
    width: '100%',
    maxWidth: '500px',
    mt: 4,
    mb: 6,
  },
  buttonStack: {
    width: '100%',
    justifyContent: 'center',
  },
  cancelButton: {
    textTransform: 'none',
    height: '40px',
    padding: '12px 24px',
  },
  submitButton: {
    textTransform: 'none',
    height: '40px',
    padding: '12px 24px',
  },
  footerSection: {
    width: '100%',
    mt: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

export const cancelConfirmationViewStyles: Record<string, SxProps<Theme>> = {
  container: {
    maxWidth: '600px',
    mx: 'auto',
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },

  contentBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    px: 2,
    py: 4,
    flex: 1,
  },
  imageContainer: {
    position: 'relative',
    width: 136,
    height: 136,
    mb: 2,
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  cancelIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 77,
    height: 82,
  },
  titleText: {
    mt: 2,
    fontWeight: 600,
    color: Color.PrimaryMain,
  },
  descriptionText: {
    mt: 2,
    color: Color.TextTertiary,
    fontWeight: 400,
    maxWidth: 400,
  },
  buttonStack: {
    mt: 4,
    width: '100%',
    maxWidth: 300,
    justifyContent: 'center',
    flexDirection: {xs: 'column', sm: 'row'},
    gap: 2,
  },
  button: {
    height: '40px',
    width: '75px',
    textTransform: 'none',
    padding: '12px 24px 12px 24px',
    gap: '12px',
  },
  footerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    mt: 'auto',
  },
};

export const paymentFailureViewStyles: Record<string, SxProps | CSSProperties> =
  {
    container: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 'calc(100vh - 160px)',
      px: 2,
      py: 8,
    },
    stack: {
      maxWidth: 520,
      width: '100%',
      textAlign: 'center',
    },
    iconContainer: {
      width: 80,
      height: 80,
      borderRadius: '50%',
      backgroundColor: '#FFF7E5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    icon: {
      width: 40,
      height: 40,
      color: '#FF9800',
    } as CSSProperties,
    title: {
      fontSize: '1.8rem',
      fontWeight: 600,
      color: '#7D33FF',
    },
    description: {
      color: '#444',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    descriptionSpacing: {
      mt: 2,
    },
    buttonContainer: {
      mt: 2,
    },
    button: {
      px: 4,
      py: 1.5,
      borderRadius: 2,
    },
  };

export default styles;
